import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { EMPTY, from, merge, timer } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';

// Captured when this file is first loaded, i.e. before the router's first navigation. An outdated cached build may
// not know the route that was opened (e.g. a new page linked from an email) and redirect away from it, so the
// reload has to go back to the address that was actually opened rather than where the old build ended up.
const OPENED_URL = window.location.href;

// Only switch to a new version this soon after opening the portal; later the person may be filling in a form, so
// the new version is left for their next visit (the service worker serves it once all portal tabs are closed).
export const STARTUP_UPDATE_WINDOW_MS = 10000;

/**
 * Makes returning visitors run the latest deployed portal (Bug 43814). The service worker serves the cached build
 * first and downloads a new one in the background, so right after a release a returning visitor gets the old
 * build until every portal tab is closed.
 */
@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private swUpdate = inject(SwUpdate);

  init(openedUrl: string = OPENED_URL): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // The cached build can no longer be served (e.g. its files were removed by a newer deployment).
    this.swUpdate.unrecoverable.subscribe(() => this.reload(openedUrl));

    const versionReady$ = this.swUpdate.versionUpdates.pipe(
      filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
    );
    // The worker may have finished downloading before this subscription; checkForUpdate() still reports it.
    const updateFound$ = from(this.swUpdate.checkForUpdate()).pipe(
      filter((found) => found),
      catchError(() => EMPTY),
    );

    merge(versionReady$, updateFound$)
      .pipe(takeUntil(timer(STARTUP_UPDATE_WINDOW_MS)), take(1))
      .subscribe(() => this.reload(openedUrl));
  }

  // A full page load (not a router navigation) so the service worker serves the new build.
  protected reload(url: string): void {
    window.location.replace(url);
  }
}
